/**
 * On-device crash diagnostics — a breadcrumb trail that SURVIVES the WKWebView
 * content-process crash + reload (localStorage is on disk, so it persists across
 * a content-process termination, a full reload, and an app relaunch).
 *
 * WHY THIS EXISTS: the iPhone boot-loop cannot be observed without a cable /
 * Safari Web Inspector. Instead, the app drops a timestamped crumb at each step
 * of the suspect path (the PreRun mount + its first paint frames). When the
 * render process dies and Capacitor reloads to the boot screen, the NEXT boot
 * reads the previous trail and paints it on screen — so the owner can screenshot
 * exactly where, and how far, the last run got before it died, and whether a JS
 * error fired (build crash) or it died silently after a clean mount (a native /
 * GPU-process crash, e.g. compositing memory blown by the heavy effects at
 * dpr-3 on a wide iPhone stage).
 *
 * EVERY consumer is gated behind the build-time `__NATIVE_SHELL__` literal, so
 * this whole module + all its call sites dead-code-eliminate out of the
 * size-gated web bundle (zero bytes there).
 */

const TRAIL_KEY = "aiwars.diag.trail.v1";
const BOOT_SEQ_KEY = "aiwars.diag.bootseq.v1";
// Headroom for a multi-reboot loop: a post-update content-process reap restarts
// the bundle several times, and each restart re-runs the full boot path (~20+
// crumbs). 120 keeps two or three whole reboot cycles readable on the overlay
// instead of shifting the earliest (and most diagnostic) ones out.
const MAX_CRUMBS = 120;

let enabled = false;

interface Crumb {
  /** epoch ms */
  t: number;
  tag: string;
}

/** Turn diagnostics on (native boot only). No-op until called. */
export function enableDiagnostics(): void {
  enabled = true;
}

/** Append a breadcrumb (synchronous + durable). Safe to call anywhere. */
export function crumb(tag: string): void {
  if (!enabled) return;
  try {
    const arr = readTrail();
    arr.push({ t: Date.now(), tag });
    while (arr.length > MAX_CRUMBS) arr.shift();
    localStorage.setItem(TRAIL_KEY, JSON.stringify(arr));
  } catch {
    /* storage blocked — diagnostics are best-effort, never throw */
  }
}

export function readTrail(): Crumb[] {
  try {
    const raw = localStorage.getItem(TRAIL_KEY);
    return raw ? (JSON.parse(raw) as Crumb[]) : [];
  } catch {
    return [];
  }
}

export function clearTrail(): void {
  try {
    localStorage.removeItem(TRAIL_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * A monotonic, DURABLE per-JS-context boot counter (localStorage-backed, so it
 * survives a WKWebView content-process termination + Capacitor reload). It bumps
 * exactly ONCE per fresh bundle evaluation — call it once at the top of `boot()`.
 *
 * This is the load-bearing discriminator for the post-update boot-loop (Defect
 * A): a NATIVE content-process reap restarts the bundle from zero (a brand-new
 * JS global), so this counter CLIMBS across the trail's successive `boot:start`
 * crumbs. A JS-internal re-entry loop re-runs the boot FLOW inside the SAME
 * context (no module re-eval), so the counter stays PUT while only `boot:enter`
 * repeats. Climbing seq ⇒ native reload; static seq + repeated boot:enter ⇒ a
 * logic loop. The two need completely different fixes, so the trail must prove
 * which before any code is touched. Best-effort: returns 0 if storage is blocked.
 */
export function nextBootContextSeq(): number {
  try {
    const n = (Number.parseInt(localStorage.getItem(BOOT_SEQ_KEY) ?? "0", 10) || 0) + 1;
    localStorage.setItem(BOOT_SEQ_KEY, String(n));
    return n;
  } catch {
    return 0;
  }
}

/**
 * Drop crumbs over the frames + seconds AFTER a mount, so a JS-build crash
 * (trail stops mid-build) is distinguishable from a first-paint / GPU-process
 * crash (build completes, then it dies on `raf1` / `+500ms`) and from a clean
 * survival (`+3000ms survived`).
 */
export function crumbFrames(label: string): void {
  if (!enabled) return;
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => {
      crumb(`${label}:raf1`);
      requestAnimationFrame(() => crumb(`${label}:raf2`));
    });
  }
  setTimeout(() => crumb(`${label}:+500ms`), 500);
  setTimeout(() => crumb(`${label}:+1500ms`), 1500);
  setTimeout(() => crumb(`${label}:+3000ms survived`), 3000);
}

function deviceLine(): string {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const dpr = window.devicePixelRatio;
  const vv = window.visualViewport;
  const vvs = vv ? ` vv ${Math.round(vv.width)}×${Math.round(vv.height)}` : "";
  return `viewport ${w}×${h} dpr ${dpr}${vvs}`;
}

/**
 * If a previous trail exists, paint it full-screen and HALT the normal boot
 * until the owner taps CONTINUE (which clears the trail and runs `onContinue`).
 * Returns true if it showed the panel (boot is now waiting on the button),
 * false if there was nothing to show (caller should proceed normally).
 */
export function showDiagnosticOverlay(onContinue: () => void): boolean {
  const trail = readTrail();
  if (trail.length === 0) return false;

  // Self-suppress once healthy: surface the panel only when the previous session
  // was actively doing something right before it rebooted — i.e. it crashed mid-
  // step.
  //
  // The CURRENT session begins at the LAST `boot:start` crumb. The earlier code
  // assumed `boot:start` was the trail's final entry, but it never is by the time
  // this runs — `applyFxLiteIfNeeded` (and now the boot:ctx / GC probes) all drop
  // crumbs right AFTER `boot:start` in the same session, so `last === "boot:start"`
  // was permanently false and the whole overlay was silently dead (which is why
  // the owner never saw a crash trail despite the loop). Anchor on the last
  // `boot:start` instead and read the crumb immediately before it — that is where
  // the PREVIOUS session was when it rebooted.
  let cur = -1;
  for (let i = trail.length - 1; i >= 0; i--) {
    if (trail[i]!.tag === "boot:start") {
      cur = i;
      break;
    }
  }
  // No earlier session ahead of this boot:start (first launch, or boot:start is
  // crumb 0) → nothing to surface.
  if (cur <= 0) return false;
  // A clean idle survival (`… survived`) or a bare back-to-back boot (a manual
  // relaunch) is not a crash-loop, so stay quiet. Anything else means the prior
  // session died mid-step (PreRun OR the board OR the boot render itself, since
  // each mounted screen ends its frame-probe with a `survived` crumb).
  const prev = trail[cur - 1]!.tag;
  if (prev.includes("survived") || prev === "boot:start") return false;

  const t0 = trail[0]!.t;
  const lines = trail
    .map((c) => {
      const d = c.t - t0;
      const mark = c.tag.startsWith("boot:") ? "⟲ " : "  ";
      return `+${String(d).padStart(6, " ")}ms ${mark}${c.tag}`;
    })
    .join("\n");

  const panel = document.createElement("div");
  panel.setAttribute("role", "dialog");
  panel.style.cssText = [
    "position:fixed",
    "inset:0",
    "z-index:2147483647",
    "background:rgb(0,0,0)",
    "color:rgb(124,255,176)",
    "font:13px/1.45 ui-monospace,Menlo,Consolas,monospace",
    "padding:16px",
    "overflow:auto",
    "-webkit-overflow-scrolling:touch",
    "white-space:pre-wrap",
    "word-break:break-word",
  ].join(";");

  const head =
    "AI WARS — CRASH DIAGNOSE\n" +
    "Verlauf der VORIGEN Session (vor dem Neustart):\n" +
    deviceLine() +
    "\nUA " +
    navigator.userAgent +
    "\n" +
    "────────────────────────────────────────\n";

  const pre = document.createElement("div");
  pre.textContent = head + lines + "\n────────────────────────────────────────\n";

  const btn = document.createElement("button");
  btn.textContent = "WEITER ▶ (Verlauf leeren & starten)";
  btn.style.cssText = [
    "margin-top:14px",
    "padding:14px 18px",
    "font:600 15px ui-monospace,monospace",
    "background:rgb(0,187,51)",
    "color:rgb(0,0,17)",
    "border:0",
    "border-radius:8px",
    "width:100%",
  ].join(";");
  btn.addEventListener("click", () => {
    clearTrail();
    panel.remove();
    onContinue();
  });

  panel.appendChild(pre);
  panel.appendChild(btn);
  document.body.appendChild(panel);
  return true;
}
